// =====================================================
// PAGE ADMIN: Statistiques d'un Coupon
// Date: 2025-11-28
// Description: Vue détaillée des performances et de l'historique d'un coupon
// =====================================================

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { 
  ArrowLeft,
  Tag,
  BarChart3,
  Loader2,
  Percent,
  ShoppingBag,
  BookOpen,
  Heart,
  Calendar,
  Users
} from 'lucide-react';
import { toast } from 'sonner';
import {
  getCouponById,
  getCouponStats,
  getCouponUsageHistory,
  type Coupon,
  type CouponUsage,
  type ModuleType
} from '../../../services/couponService';

const moduleLabels: Record<ModuleType, string> = {
  shop: 'Boutique',
  elearning: 'E-learning',
  psy: 'Psy'
};

const moduleIcons: Record<ModuleType, React.ElementType> = {
  shop: ShoppingBag,
  elearning: BookOpen,
  psy: Heart
};

const isNeverExpires = (validUntil: string) => new Date(validUntil).getFullYear() >= 2099;

export default function CouponStats() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState(true);
  const [coupon, setCoupon] = useState<Coupon | null>(null);
  const [stats, setStats] = useState<{
    total_usages: number;
    total_discount_given: number;
    total_revenue_generated: number;
    usages_by_module: Record<ModuleType, number>;
  } | null>(null);
  const [history, setHistory] = useState<CouponUsage[]>([]);

  useEffect(() => {
    if (!id) return;
    loadData(id);
  }, [id]);

  const loadData = async (couponId: string) => {
    try {
      setLoading(true);
      const [couponData, statsData, historyData] = await Promise.all([
        getCouponById(couponId),
        getCouponStats(couponId),
        getCouponUsageHistory(couponId, 30)
      ]);

      if (!couponData) {
        toast.error('Coupon introuvable');
        navigate('/admin/coupons');
        return;
      }

      setCoupon(couponData);
      setStats(statsData);
      setHistory(historyData);
    } catch (error) {
      console.error('Erreur chargement stats coupon:', error);
      toast.error("Erreur lors du chargement des statistiques du coupon");
      navigate('/admin/coupons');
    } finally {
      setLoading(false);
    }
  };

  if (loading || !coupon || !stats) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  const totalUsages = stats.total_usages;
  const totalDiscount = stats.total_discount_given;
  const totalRevenue = stats.total_revenue_generated;

  return (
    <AdminLayout>
      <div className="p-4 md:p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <button
              onClick={() => navigate('/admin/coupons')}
              className="flex items-center gap-2 text-gray-400 hover:text-white mb-3 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour à la liste
            </button>
            <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
              <Tag className="w-7 h-7 text-[#D4AF37]" />
              Statistiques du coupon
            </h1>
            <p className="text-gray-400 mt-1">
              Code <span className="font-mono font-semibold text-white bg-white/10 px-2 py-0.5 rounded">{coupon.code}</span> 
              · {coupon.name}
            </p>
          </div>
          <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
            <BarChart3 className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-sm text-gray-200">Vue statistiques</span>
          </div>
        </div>

        {/* Infos principales */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 space-y-2">
            <p className="text-xs text-gray-400 uppercase tracking-wide">Type de réduction</p>
            <div className="flex items-center gap-2">
              <Percent className="w-4 h-4 text-[#D4AF37]" />
              <p className="text-white font-semibold">
                {coupon.discount_type === 'percentage'
                  ? `${coupon.discount_value}%`
                  : `${coupon.discount_value.toLocaleString('fr-FR')} FCFA`}
              </p>
            </div>
            {coupon.min_purchase_amount > 0 && (
              <p className="text-xs text-gray-400">
                Min. achat: {coupon.min_purchase_amount.toLocaleString('fr-FR')} FCFA
              </p>
            )}
            {coupon.discount_type === 'percentage' && coupon.max_discount_amount !== null && (
              <p className="text-xs text-gray-400">
                Plafond: {coupon.max_discount_amount.toLocaleString('fr-FR')} FCFA
              </p>
            )}
          </div>

          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 space-y-2">
            <p className="text-xs text-gray-400 uppercase tracking-wide">Période de validité</p>
            <div className="text-sm text-white">
              <p>
                Du {new Date(coupon.valid_from).toLocaleDateString('fr-FR')}<br />
                {isNeverExpires(coupon.valid_until)
                  ? `N'expire jamais`
                  : `au ${new Date(coupon.valid_until).toLocaleDateString('fr-FR')}`}
              </p>
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Statut: {coupon.is_active ? <span className="text-green-400">Actif</span> : <span className="text-red-400">Inactif</span>}
            </p>
          </div>

          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 space-y-2">
            <p className="text-xs text-gray-400 uppercase tracking-wide">Limites d'utilisation</p>
            <p className="text-sm text-white">
              Globale: {coupon.usage_limit ? `${coupon.usage_count}/${coupon.usage_limit}` : `${coupon.usage_count} (illimité)`}
            </p>
            <p className="text-sm text-gray-200">
              Par utilisateur: {coupon.usage_limit_per_user || 0}
            </p>
            <div className="text-xs text-gray-400 mt-1">
              Modules: {coupon.applies_to_modules.map((m) => moduleLabels[m]).join(', ')}
            </div>
          </div>
        </div>

        {/* Cartes de stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 flex flex-col justify-between">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-400 uppercase tracking-wide">Utilisations totales</span>
              <Users className="w-4 h-4 text-[#D4AF37]" />
            </div>
            <p className="text-3xl font-bold text-white">{totalUsages}</p>
          </div>

          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 flex flex-col justify-between">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-400 uppercase tracking-wide">Réductions accordées</span>
              <Percent className="w-4 h-4 text-[#D4AF37]" />
            </div>
            <p className="text-2xl font-bold text-white">{totalDiscount.toLocaleString('fr-FR')} FCFA</p>
          </div>

          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 flex flex-col justify-between">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-400 uppercase tracking-wide">Chiffre d'affaires généré</span>
              <BarChart3 className="w-4 h-4 text-[#D4AF37]" />
            </div>
            <p className="text-2xl font-bold text-white">{totalRevenue.toLocaleString('fr-FR')} FCFA</p>
          </div>
        </div>

        {/* Répartition par module */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#D4AF37]" />
            Répartition des utilisations par module
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {(Object.keys(stats.usages_by_module) as ModuleType[]).map((module) => {
              const Icon = moduleIcons[module];
              const value = stats.usages_by_module[module];
              const percentage = totalUsages > 0 ? Math.round((value / totalUsages) * 100) : 0;
              return (
                <div key={module} className="bg-white/5 rounded-xl border border-white/10 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="w-5 h-5 text-[#D4AF37]" />
                    <span className="text-sm text-white font-medium">{moduleLabels[module]}</span>
                  </div>
                  <p className="text-2xl font-bold text-white">{value}</p>
                  <p className="text-xs text-gray-400">{percentage}% des utilisations</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Historique des dernières utilisations */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#D4AF37]" />
            Dernières utilisations
          </h2>

          {history.length === 0 ? (
            <p className="text-gray-400 text-sm">Aucune utilisation pour le moment.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10 text-gray-400 text-xs uppercase">
                    <th className="px-3 py-2 text-left">Date</th>
                    <th className="px-3 py-2 text-left">Utilisateur</th>
                    <th className="px-3 py-2 text-left">Module</th>
                    <th className="px-3 py-2 text-right">Montant initial</th>
                    <th className="px-3 py-2 text-right">Réduction</th>
                    <th className="px-3 py-2 text-right">Montant final</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {history.map((usage) => {
                    const Icon = moduleIcons[usage.module_type as ModuleType];
                    return (
                      <tr key={usage.id} className="hover:bg-white/5">
                        <td className="px-3 py-2 text-gray-200">
                          {usage.created_at
                            ? new Date(usage.created_at).toLocaleString('fr-FR', {
                                year: 'numeric',
                                month: 'short',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit'
                              })
                            : '-'}
                        </td>
                        <td className="px-3 py-2 text-gray-200">{usage.user_email}</td>
                        <td className="px-3 py-2 text-gray-200">
                          <div className="flex items-center gap-1">
                            <Icon className="w-4 h-4 text-[#D4AF37]" />
                            <span>{moduleLabels[usage.module_type as ModuleType]}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right text-gray-200">
                          {usage.original_amount.toLocaleString('fr-FR')} FCFA
                        </td>
                        <td className="px-3 py-2 text-right text-gray-200">
                          -{usage.discount_amount.toLocaleString('fr-FR')} FCFA
                        </td>
                        <td className="px-3 py-2 text-right text-gray-200">
                          {usage.final_amount.toLocaleString('fr-FR')} FCFA
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
